import React, { useEffect, useState } from 'react';
import { fetchAndNormalize, fetchMaterialById, normalizeRow } from './api/materials';
import Notes from './tabs/Notes';
import Pollution from './tabs/Pollution';
import Temperature from './tabs/Temperature';

export default function MaterialDetail({ material, materialId }) {
  const [local, setLocal] = useState(material ? (material._raw ? normalizeRow(material._raw) : material) : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('notes');

  useEffect(() => {
    let mounted = true;
    if (material) {
      setLocal(material._raw ? normalizeRow(material._raw) : material);
      return () => { mounted = false; };
    }
    async function loadById(id) {
      setLoading(true);
      setError(null);
      try {
        const found = await fetchMaterialById(id);
        if (!mounted) return;
        if (!found) {
          setError('Nie znaleziono materiału');
          setLocal(null);
        } else {
          setLocal(found);
        }
      } catch (err) {
        if (!mounted) return;
        setError(err.message || 'Błąd pobierania materiału');
        setLocal(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    if (materialId) loadById(materialId);
    return () => { mounted = false; };
  }, [material, materialId]);

  if (loading) return <div className="alert alert-info">Ładowanie szczegółów…</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!local) return null;

  const meta = (local._raw && local._raw.metadata)
    ? (typeof local._raw.metadata === 'string' ? (() => { try { return JSON.parse(local._raw.metadata); } catch { return {}; } })() : local._raw.metadata)
    : {};

  return (
    <div className="card">
      <div className="card-body">
        <h5 className="card-title">{local.name}</h5>
        <h6 className="card-subtitle mb-2 text-muted">{local.code ?? ''}</h6>

        <div className="row mb-3">
            <div className="col-sm-6">
              <strong>Wymaga komory:</strong>
              <div>{local.enclosedLabel ?? (meta && meta.enclosedLabel) ?? 'Nieznany'}</div>
            </div>
          <div className="col-sm-6">
            <strong>Gęstość (g/cm³):</strong>
            <div>{local.density ?? '-'}</div>
          </div>
        </div>

        <div className="row mb-3">
            <div className="col-sm-0">
              <strong>Notatki</strong>
              <p className="small mb-2">{local.notes ?? '-'}</p>
            </div>
        </div>

        <div className="mb-3">
          <ul className="nav nav-tabs mb-2" role="tablist">
            <li className="nav-item" role="presentation">
              <button className={`nav-link ${activeTab === 'notes' ? 'active' : ''}`} onClick={() => setActiveTab('notes')} type="button">Właściwości</button>
            </li>
            <li className="nav-item" role="presentation">
              <button className={`nav-link ${activeTab === 'contaminants' ? 'active' : ''}`} onClick={() => setActiveTab('contaminants')} type="button">Zanieczyszczenia</button>
            </li>
            <li className="nav-item" role="presentation">
              <button className={`nav-link ${activeTab === 'temperature' ? 'active' : ''}`} onClick={() => setActiveTab('temperature')} type="button">Temperatura</button>
            </li>
          </ul>

          <div className="tab-content">
            <div className={`tab-pane ${activeTab === 'notes' ? 'active' : ''}`}>
              <Notes
                notes={local.notes}
                max_print_speed={local.max_print_speed}
                hardness={local.hardness}
                hardness_scale={local.hardness_scale}
                impact_strength={local.impact_strength}
                tensile_strength={local.tensile_strength}
                use_adhesive={local.use_adhesive}
                additional_properties={local.additional_properties ?? local.chemical_properties}
              />
            </div>

            <div className={`tab-pane ${activeTab === 'contaminants' ? 'active' : ''}`}>
              <Pollution local={local} meta={meta} />
            </div>

            <div className={`tab-pane ${activeTab === 'temperature' ? 'active' : ''}`}>
              <Temperature
                temp_min={local.temp_min}
                temp_max={local.temp_max}
                bed_temp_min={local.bed_temp_min}
                bed_temp_max={local.bed_temp_max}
                fan_speed_min={local.fan_speed_min}
                fan_speed_max={local.fan_speed_max}
                notes={meta.temperatureNotes}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
